import { BffUnauthenticatedError, bffFetch } from "@/shared/api/client";
import { BffNoAdvisorError, requireAdvisor } from "@/shared/api/identity";
import {
  approvalDecisionBody,
  DEFAULT_REATTACH_LIMITS,
  SseFrameScanner,
  type ApprovalDecisionInput,
  type IntelligenceFrame,
  type ToolResultInput,
} from "@/features/intelligence/api/frames";
import {
  relayIntelligenceStream,
  relayStopLog,
  type RelayOpen,
} from "@/features/intelligence/api/relay";
import {
  conversationFromBody,
  createConversationBody,
  patchConversationBody,
  postMessageBody,
  resolvedFault,
  toConversation,
  toConversationSummaries,
  type IntelligenceAttachmentRef,
  type IntelligenceConversation,
  type IntelligenceConversationSummary,
  type PostMessageInput,
} from "@/features/intelligence/api/shapes";

const CONVERSATIONS = "v1/intelligence/conversations";

const SSE = { Accept: "text/event-stream" };

const MAX_TOOL_RESULT_CHARS = 8000;

export const STREAM_SILENCE_MS = 60_000;

export interface ChatCallError {
  status: number;
  code: string | null;
}

export type ChatResult<T> = { ok: true; value: T } | { ok: false; error: ChatCallError };

export function fetchConversations(): Promise<ChatResult<IntelligenceConversationSummary[]>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}?limit=50`);
    return json(res, toConversationSummaries);
  });
}

export function fetchPinnedConversations(): Promise<ChatResult<IntelligenceConversationSummary[]>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/pinned`);
    return json(res, toConversationSummaries);
  });
}

export function pinConversation(id: string): Promise<ChatResult<null>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}/pin`, { method: "PUT" });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: null };
  });
}

export function unpinConversation(id: string): Promise<ChatResult<null>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}/pin`, { method: "DELETE" });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: null };
  });
}

export function updatePinnedConversations(ids: string[]): Promise<ChatResult<null>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/pinned`, {
      method: "PUT",
      json: { ids },
    });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: null };
  });
}

export function fetchConversation(id: string): Promise<ChatResult<IntelligenceConversation>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}`);
    if (!res.ok) return { ok: false, error: await failure(res) };
    const conversation = toConversation(await res.json());
    if (!conversation) {
      return { ok: false, error: { status: 502, code: "unparsable_conversation" } };
    }
    return { ok: true, value: conversation };
  });
}

export function createConversation(
  locale: "sv" | "en",
): Promise<ChatResult<{ id: string; conversation: IntelligenceConversation | null }>> {
  return call(async () => {
    const res = await bffFetch(CONVERSATIONS, {
      method: "POST",
      json: createConversationBody({ locale }),
    });
    if (!res.ok) return { ok: false, error: await failure(res) };
    const conversation = conversationFromBody(await res.text());
    const id = conversation?.id ?? idFromLocation(res);
    if (!id) return { ok: false, error: { status: 502, code: "created_without_id" } };
    return { ok: true, value: { id, conversation } };
  });
}

export function renameConversation(
  id: string,
  title: string | null,
): Promise<ChatResult<IntelligenceConversation | null>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}`, {
      method: "PATCH",
      json: patchConversationBody({ title }),
    });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: conversationFromBody(await res.text()) };
  });
}

export function deleteConversation(id: string): Promise<ChatResult<null>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}`, { method: "DELETE" });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: null };
  });
}

export function cancelTurn(id: string): Promise<ChatResult<{ delivered: boolean }>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}/messages/cancel`, { method: "POST" });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: { delivered: responseConfirmsDelivery(await res.text()) } };
  });
}

export function sendApprovalDecision(
  id: string,
  runId: string,
  decision: ApprovalDecisionInput,
): Promise<ChatResult<{ delivered: boolean }>> {
  return call(async () => {
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}/runs/${seg(runId)}/approvals`, {
      method: "POST",
      json: approvalDecisionBody(decision),
    });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: { delivered: responseConfirmsDelivery(await res.text()) } };
  });
}

export function sendToolResult(
  id: string,
  runId: string,
  outcome: ToolResultInput,
): Promise<ChatResult<{ delivered: boolean }>> {
  return call(async () => {
    const text = "result" in outcome ? outcome.result : outcome.error;
    if (text.trim() === "" || text.length > MAX_TOOL_RESULT_CHARS) {
      return { ok: false, error: { status: 400, code: "tool_result_size" } };
    }
    const body: Record<string, unknown> = { tool_call_id: outcome.toolCallId };
    if ("result" in outcome) body.result = outcome.result;
    else body.error = outcome.error;
    const res = await bffFetch(`${CONVERSATIONS}/${seg(id)}/runs/${seg(runId)}/tool-results`, {
      method: "POST",
      json: body,
    });
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: { delivered: responseConfirmsDelivery(await res.text()) } };
  });
}

export function uploadAttachment(
  conversationId: string,
  file: File,
  signal?: AbortSignal,
): Promise<ChatResult<IntelligenceAttachmentRef>> {
  return call(async () => {
    const filename = file.name || "upload";
    const form = new FormData();
    form.append("file", file, filename);
    const res = await bffFetch(`${CONVERSATIONS}/${seg(conversationId)}/attachments`, {
      method: "POST",
      body: form,
      signal,
    });
    if (!res.ok) return { ok: false, error: await failure(res) };

    const text = await res.text();
    let raw: Record<string, unknown> | null = null;
    try {
      raw = text ? (JSON.parse(text) as Record<string, unknown>) : null;
    } catch {
      raw = null;
    }
    const id = (typeof raw?.id === "string" ? raw.id : "") || idFromLocation(res);
    if (!id) return { ok: false, error: { status: 502, code: "uploaded_without_id" } };
    return {
      ok: true,
      value: {
        id,
        filename: typeof raw?.filename === "string" && raw.filename ? raw.filename : filename,
        mimeType: typeof raw?.mime_type === "string" ? raw.mime_type : file.type,
        size: typeof raw?.size === "number" ? raw.size : file.size,
      },
    };
  });
}

export function readAttachment(
  conversationId: string,
  attachmentId: string,
  signal?: AbortSignal,
): Promise<ChatResult<IntelligenceAttachmentRef>> {
  return call(async () => {
    const res = await bffFetch(
      `${CONVERSATIONS}/${seg(conversationId)}/attachments/${seg(attachmentId)}`,
      { signal },
    );
    if (!res.ok) return { ok: false, error: await failure(res) };
    const raw = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    const id = typeof raw?.id === "string" ? raw.id : "";
    if (!id) return { ok: false, error: { status: 502, code: "read_without_id" } };
    return {
      ok: true,
      value: {
        id,
        filename: typeof raw?.filename === "string" ? raw.filename : "",
        mimeType: typeof raw?.mime_type === "string" ? raw.mime_type : "",
        size: typeof raw?.size === "number" ? raw.size : 0,
      },
    };
  });
}

export function fetchAttachmentContent(
  conversationId: string,
  attachmentId: string,
): Promise<ChatResult<Blob>> {
  return call(async () => {
    const res = await bffFetch(
      `${CONVERSATIONS}/${seg(conversationId)}/attachments/${seg(attachmentId)}/content`,
    );
    if (!res.ok) return { ok: false, error: await failure(res) };
    return { ok: true, value: await res.blob() };
  });
}

export function attachmentErrorKey(error: ChatCallError): string {
  if (error.status === 401) return "intelligence.attachmentOpenExpired";
  if (error.status === 403) return "intelligence.attachmentOpenForbidden";
  if (error.status === 404) return "intelligence.attachmentOpenGone";
  return "intelligence.attachmentOpenFailed";
}

export type StreamResult = ChatResult<ReadableStream<Uint8Array>>;

export function openSendStream(
  id: string,
  input: {
    content: string;
    locale: "sv" | "en";
    parentId?: string | null;
    frontendContext?: object | null;
    tools?: object[] | null;
    attachmentIds?: string[] | null;
  },
  signal: AbortSignal,
): Promise<StreamResult> {
  const message: PostMessageInput = { content: input.content, locale: input.locale };
  if ("parentId" in input) message.parentId = input.parentId;
  if (input.frontendContext) message.frontendContext = input.frontendContext;
  if (input.tools && input.tools.length > 0) message.tools = input.tools;
  if (input.attachmentIds && input.attachmentIds.length > 0) {
    message.attachmentIds = input.attachmentIds;
  }
  return openStream(
    id,
    "send",
    () =>
      bffFetch(`${CONVERSATIONS}/${seg(id)}/messages`, {
        method: "POST",
        headers: SSE,
        json: postMessageBody(message),
        signal,
      }),
    signal,
  );
}

export function openRegenerateStream(
  id: string,
  messageId: string,
  signal: AbortSignal,
): Promise<StreamResult> {
  return openStream(
    id,
    "regenerate",
    () =>
      bffFetch(`${CONVERSATIONS}/${seg(id)}/messages/${seg(messageId)}/regenerate`, {
        method: "POST",
        headers: SSE,
        signal,
      }),
    signal,
  );
}

export function openRunStream(
  id: string,
  runId: string,
  lastEventId: string | null,
  signal: AbortSignal,
): Promise<StreamResult> {
  return openStream(id, "attach", () => runStreamRequest(id, runId, lastEventId, signal), signal, {
    runId,
    lastEventId,
  });
}

export async function readFrames(
  body: ReadableStream<Uint8Array>,
  onFrame: (frame: IntelligenceFrame) => void,
  silenceMs: number = STREAM_SILENCE_MS,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const scanner = new SseFrameScanner();
  let silence: ReturnType<typeof setTimeout> | undefined;
  const listen = () => {
    clearTimeout(silence);
    silence = setTimeout(() => void reader.cancel().catch(() => {}), silenceMs);
  };
  try {
    listen();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      listen();
      if (!value) continue;
      for (const frame of scanner.push(decoder.decode(value, { stream: true }))) {
        onFrame(frame);
      }
    }
  } catch {
  } finally {
    clearTimeout(silence);
    try {
      reader.releaseLock();
    } catch {}
  }
}

async function call<T>(run: () => Promise<ChatResult<T>>): Promise<ChatResult<T>> {
  try {
    await requireAdvisor();
    return await run();
  } catch (err) {
    return { ok: false, error: thrown(err) };
  }
}

async function json<T>(res: Response, pick: (body: unknown) => T): Promise<ChatResult<T>> {
  if (!res.ok) return { ok: false, error: await failure(res) };
  return { ok: true, value: pick((await res.json()) as unknown) };
}

async function failure(res: Response): Promise<ChatCallError> {
  const text = await res.text().catch(() => "");
  const { status, code } = resolvedFault(res.status, text);
  return answerOf(status, code);
}

function answerOf(status: number, code: string | null): ChatCallError {
  if (status === 404 || status === 422) return { status: 404, code };
  if (status === 400 || status === 401 || status === 403 || status === 409) return { status, code };
  return { status: 502, code };
}

function thrown(err: unknown): ChatCallError {
  if (err instanceof BffUnauthenticatedError) return { status: 401, code: "SESSION_EXPIRED" };
  if (err instanceof BffNoAdvisorError) return { status: 403, code: "NO_ADVISOR" };
  return { status: 502, code: "BFF_UNREACHABLE" };
}

function idFromLocation(res: Response): string {
  return res.headers.get("location")?.split("/").filter(Boolean).pop() ?? "";
}

function responseConfirmsDelivery(text: string): boolean {
  if (!text) return false;
  try {
    return (JSON.parse(text) as { delivered?: unknown }).delivered === true;
  } catch {
    return false;
  }
}

function openStream(
  id: string,
  op: string,
  request: () => Promise<Response>,
  signal: AbortSignal,
  resume?: { runId?: string | null; lastEventId?: string | null } | null,
): Promise<StreamResult> {
  return call(async () => {
    const res = await request();
    if (!res.ok) return { ok: false, error: await failure(res) };
    if (!res.body) return { ok: false, error: { status: 502, code: "stream_without_body" } };
    return { ok: true, value: relayed(id, op, res.body, signal, resume) };
  });
}

function relayed(
  id: string,
  op: string,
  first: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  resume?: { runId?: string | null; lastEventId?: string | null } | null,
): ReadableStream<Uint8Array> {
  return relayIntelligenceStream(first, {
    limits: DEFAULT_REATTACH_LIMITS,
    resume,
    signal,
    openers: {
      async reattach(runId, lastEventId, sig): Promise<RelayOpen> {
        let res: Response;
        try {
          res = await runStreamRequest(id, runId, lastEventId, sig);
        } catch (err) {
          const e = thrown(err);
          return { ok: false, status: e.status, code: e.code };
        }
        if (!res.ok) {
          const e = await failure(res);
          return { ok: false, status: e.status, code: e.code };
        }
        if (!res.body) return { ok: false, status: 502, code: "stream_without_body" };
        return { ok: true, body: res.body };
      },
    },
    hooks: {
      onEnd(summary) {
        const { level, line } = relayStopLog(op, id, summary);
        if (level === "warn") console.warn(line);
        else console.info(line);
      },
    },
  });
}

function runStreamRequest(
  id: string,
  runId: string,
  lastEventId: string | null,
  signal: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = { ...SSE };
  if (lastEventId) headers["Last-Event-ID"] = lastEventId;
  return bffFetch(`${CONVERSATIONS}/${seg(id)}/runs/${seg(runId)}/stream`, {
    method: "GET",
    headers,
    signal,
  });
}

function seg(id: string): string {
  return encodeURIComponent(id);
}
